import * as React from 'react';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, useAtomValue, useUpdateAtom } from 'jotai/utils';

import type { NavSection } from './navItems';

const MOBILE_BREAKPOINT_PX = 768;

/**
 * Per-section fold state for the expanded sidebar (persisted across
 * reloads). A section whose flag is `true` renders only its header; the
 * item list is hidden until the user clicks the header to unfold it.
 *
 * `advancedTools` and `configure` default to folded so the one-off
 * operational tools and admin/configuration links don't crowd the nav by
 * default.
 *
 * Storage key bumped to `.v2` when Quick tools became Advanced tools: the
 * persisted map keyed the OLD section names, so without the bump existing
 * users would get the renamed section unfolded (a stale `quickTools: true`
 * says nothing about `advancedTools`) and defaults would never re-apply.
 */
export const foldedSectionsAtom = atomWithStorage<Partial<Record<NavSection, boolean>>>(
  'filespin.dashboard.sidebar.foldedSections.v2',
  { advancedTools: true, configure: true },
);

/**
 * User-intent collapse state (persisted across reloads + sessions).
 *
 * The collapsed/expanded state is fully user-controlled. We deliberately
 * do **not** auto-collapse on chat-open: doing so silently overrode the
 * user's persisted choice and made the sidebar's collapse toggle look
 * inactive (clicks didn't change anything visible), which was confusing.
 * If the user wants more content space while chat is open they can
 * collapse the sidebar themselves with the chevron.
 */
export const sidebarCollapsedAtom = atomWithStorage<boolean>(
  'filespin.dashboard.sidebar.collapsed',
  false,
);

/** Whether the mobile drawer is open. Not persisted; not coupled to the
 *  desktop collapsed state. */
export const sidebarMobileOpenAtom = atom<boolean>(false);

/**
 * Transient "keep the sidebar to its rail while this is on screen" request,
 * used by the full-screen asset detail overlay: the rail keeps the app
 * anchored, but 240px of nav labels next to a modal the user can't navigate
 * from is wasted width.
 *
 * Deliberately NOT written into `sidebarCollapsedAtom`. Overwriting the
 * persisted choice is what previously made the collapse toggle look broken
 * (see above), and it would also mean a refresh with the overlay open left the
 * sidebar collapsed forever. Clearing this restores whatever the user had —
 * that is the whole "go back to the previous state" mechanism, no save/restore
 * bookkeeping needed.
 */
export const sidebarForcedCollapsedAtom = atom<boolean>(false);

/**
 * Requests the rail for as long as `active` is true, releasing it on cleanup.
 * Safe to mount in several places at once only if they agree — the last
 * effect to run wins, which is fine for the single overlay using it today.
 */
export function useForcedCollapsedSidebar(active: boolean) {
  const setForced = useUpdateAtom(sidebarForcedCollapsedAtom);

  React.useEffect(() => {
    if (!active) return;
    setForced(true);
    return () => setForced(false);
  }, [active, setForced]);
}

/**
 * Sidebar collapse state.
 *
 * On desktop the sidebar shows the user's persisted choice from
 * `sidebarCollapsedAtom`, unless something on screen is transiently asking for
 * the rail (`sidebarForcedCollapsedAtom` — today only the asset detail
 * overlay). Toggling while forced still works and wins: it releases the force
 * and writes the user's new intent, so the control never looks dead.
 *
 * On mobile (< 768px) the sidebar is rendered as a fixed-position
 * drawer. The `mobileOpen` state is independent of the desktop
 * `collapsed` state — opening the drawer doesn't write to the persisted
 * atom, so it doesn't bleed into the desktop layout when the viewport
 * grows again.
 */
export function useSidebarCollapse() {
  const [persistedCollapsed, setPersistedCollapsed] = useAtom(sidebarCollapsedAtom);
  const [mobileOpen, setMobileOpen] = useAtom(sidebarMobileOpenAtom);
  const forcedCollapsed = useAtomValue(sidebarForcedCollapsedAtom);
  const setForcedCollapsed = useUpdateAtom(sidebarForcedCollapsedAtom);
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MOBILE_BREAKPOINT_PX;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      const next = window.innerWidth < MOBILE_BREAKPOINT_PX;
      setIsMobile((cur) => {
        if (cur === next) return cur;
        if (!next) setMobileOpen(false);
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setMobileOpen]);

  const collapsed = !isMobile && (persistedCollapsed || forcedCollapsed);

  // Both writers release the transient force first: an explicit user action on
  // the sidebar outranks an overlay's request for the rail, and without this
  // the chevron would appear to do nothing while a modal is open.
  const setCollapsed = React.useCallback(
    (next: boolean) => {
      setForcedCollapsed(false);
      setPersistedCollapsed(next);
    },
    [setForcedCollapsed, setPersistedCollapsed],
  );

  const toggleCollapsed = React.useCallback(() => {
    setForcedCollapsed(false);
    // Toggle the state the user can SEE, which is the forced one while an
    // overlay is up — `!persistedCollapsed` would be a no-op there.
    setPersistedCollapsed(!collapsed);
  }, [collapsed, setForcedCollapsed, setPersistedCollapsed]);

  const openMobile = React.useCallback(() => setMobileOpen(true), [setMobileOpen]);
  const closeMobile = React.useCallback(() => setMobileOpen(false), [setMobileOpen]);
  const toggleMobile = React.useCallback(
    () => setMobileOpen((cur) => !cur),
    [setMobileOpen],
  );

  return {
    collapsed,
    setCollapsed,
    toggleCollapsed,
    isMobile,
    mobileOpen,
    openMobile,
    closeMobile,
    toggleMobile,
  };
}

export default useSidebarCollapse;
